import { useAuthContext } from '@repo/user-session';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useRelayEnvironment, fetchQuery, useLazyLoadQuery } from 'react-relay';

import type { hometaxBlockNotificationStatusQuery } from '@/__generated__/hometaxBlockNotificationStatusQuery.graphql';
import type { hometaxBlockSettingsQuery } from '@/__generated__/hometaxBlockSettingsQuery.graphql';
import { hometaxBlockSettings, hometaxBlockNotificationStatus } from '@/graphql/query/common/hometax-block';
import { hometaxBlockSettingsAtom } from '@/lib/stores/hometax-block';
import { BusinessType } from '@/lib/types/request';

export const useHometaxBlockSettings = (isUsePolling: boolean = false) => {
  const environment = useRelayEnvironment();
  const { auth } = useAuthContext();
  const [settings, setSettings] = useAtom(hometaxBlockSettingsAtom);
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);

  const fetchSettings = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setSettings((prev) => ({
      data: prev.data,
      loading: true,
      lastFetched: prev.lastFetched
    }));
    try {
      const data = await fetchQuery<hometaxBlockSettingsQuery>(
        environment,
        hometaxBlockSettings,
        {},
        {
          fetchPolicy: 'network-only',
          networkCacheConfig: { force: true }
        }
      ).toPromise();
      setSettings({
        data: data?.hometaxBlockSettings ?? null,
        loading: false,
        lastFetched: Date.now()
      });
    } catch (e) {
      setSettings((prev) => ({ ...prev, loading: false }));
      throw e;
    } finally {
      inFlightRef.current = false;
    }
  }, [environment, setSettings]);

  useEffect(() => {
    if (isUsePolling && !pollingTimerRef.current && auth.status === 'login') {
      fetchSettings();
      pollingTimerRef.current = setInterval(() => fetchSettings().catch(console.error), 5 * 60 * 1000);
    }
    return () => {
      if (isUsePolling && pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [auth.status, fetchSettings, isUsePolling]);

  return {
    settings: settings.data,
    loading: settings.loading,
    refresh: fetchSettings
  };
};

export const useHometaxBlockNotificationStatus = (businessType: BusinessType) => {
  const data = useLazyLoadQuery<hometaxBlockNotificationStatusQuery>(
    hometaxBlockNotificationStatus,
    { businessType },
    { fetchPolicy: 'network-only', networkCacheConfig: { force: true } }
  );
  return data.hometaxBlockNotificationStatus;
};
